import React from 'react';

import type { ButtonProps } from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import type { TooltipButtonProps } from 'components/QButton/TooltipSubmitButton';
import TooltipSubmitButton from 'components/QButton/TooltipSubmitButton';
import QButton from 'components/QButtonNew';

type StdDialogActionProps = {
  divider?: boolean;
  primary: TooltipButtonProps & {
    children?: any;
    id?: string;
    onClick?: (e?: any) => void;
    type?: string;
    variant?: string;
  };
  secondary?: ButtonProps & {
    children?: any;
    id?: string;
    onClick?: (e?: any) => void;
    variant?: string;
  };
  secondaryNode?: any;
  tertiaryAction?: ButtonProps & {
    children?: any;
    onClick?: (e?: any) => void;
  };
  tertiaryNode?: any;
  colorBar?: string;
};

const StdDialogActions = (props: StdDialogActionProps) => {
  const theme = useTheme();

  const {
    divider,
    primary: {
      children: primaryChildren = 'Confirm',
      id: primaryId = 'primary-button',
      onClick: primaryOnClick,
      type: primaryType = 'submit',
      variant: primaryVariant = 'contained',
      showTooltip,
      tooltipText,
      ...primaryOther
    } = {},
    secondary: {
      children: secondaryChildren = 'Cancel',
      id: secondaryId = 'secondary-button',
      onClick: secondaryOnClick,
      variant: secondaryVariant = 'outlined',
      ...secondaryOther
    } = {},
    secondaryNode,
    tertiaryAction: { children: tertiaryActionChildren, onClick: tertiaryActionOnClick, ...tertiaryActionOther } = {},
    tertiaryNode,
    colorBar = theme.palette.background.paper,
  } = props;

  // will inject standard error tooltip if showTooltip/tooltipText provided
  const doTooltip = Boolean(showTooltip || tooltipText);
  const PrimaryButton = doTooltip ? TooltipSubmitButton : QButton;
  const tooltipProps = doTooltip ? { tooltipText, showTooltip } : {};

  //  Set Z-Index to 1 instead of 0 so it is above TextField labels
  return (
    <Stack sx={{ position: 'sticky', bottom: '0', backgroundColor: colorBar, zIndex: 1 }}>
      {divider && <Divider />}
      <DialogActions>
        <Stack direction='row' flex='1' alignItems='center' justifyContent='space-between'>
          {tertiaryActionChildren && tertiaryActionOnClick && (
            <QButton onClick={tertiaryActionOnClick} {...tertiaryActionOther}>
              {tertiaryActionChildren}
            </QButton>
          )}
          {tertiaryNode}
          <Stack
            direction='row'
            sx={{
              marginLeft: 'auto',
              '& > :not(:first-of-type)': { ml: 2 },
            }}
          >
            {secondaryChildren && secondaryOnClick && (
              <QButton id={secondaryId} onClick={secondaryOnClick} variant={secondaryVariant} {...secondaryOther}>
                {secondaryChildren}
              </QButton>
            )}
            {secondaryNode}
            {(primaryType === 'submit' || primaryOnClick) && (
              <PrimaryButton
                id={primaryId}
                onClick={primaryOnClick}
                type={primaryType}
                variant={primaryVariant}
                {...tooltipProps}
                {...primaryOther}
              >
                {primaryChildren}
              </PrimaryButton>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </Stack>
  );
};

StdDialogActions.displayName = 'StdDialogActions';

export default StdDialogActions;
